import React, {
  useContext,
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  useMemo
} from "react";
import { motion } from "framer-motion";
import { ProductsContext } from "../context/products.context";

const YEAR_OPTIONS = [
  { value: "2025", label: "2025" },
  { value: "2024", label: "2024" },
  { value: "2023", label: "2023" },
  { value: "2022", label: "2022" },
  { value: "2021", label: "2021" },
];

/**
 * Year Selector for CipEquity with sliding animation
 * Shows visual indication when years don't have data
 */
const CipEquityYearSelector = ({ cipEquityData }) => {
  const {
    selectedFilterIndicatorDate,
    setSelectedFilterIndicatorDate,
    selectedSchool,
    currentCip
  } = useContext(ProductsContext);

  // State to track which years have data for the current selection
  const [yearsWithData, setYearsWithData] = useState(new Set());

  // Refs
  const containerRef = useRef(null);
  const labelRefs = useRef([]);

  // Local state for the slider
  const [sliderStyles, setSliderStyles] = useState({ width: 0, left: 0 });

  // Find available years when school or CIP data changes
  useEffect(() => {
    if (!cipEquityData || !Array.isArray(cipEquityData)) {
      setYearsWithData(new Set());
      return;
    }

    // Find the CIP data for the current CIP code
    const cipData = cipEquityData.find((cip) => cip.cipcode === currentCip);

    if (!cipData || !cipData.enrollment) {
      setYearsWithData(new Set());
      return;
    }

    const availableYears = new Set();
    const schoolValue = selectedSchool?.value || 'District Total';

    if (schoolValue === 'District Total') {
      // For district total, include any year that has data in any school
      cipData.enrollment.forEach(school => {
        if (school['All']) {
          school['All'].forEach((count, idx) => {
            if (count && count > 0) {
              // Year mapping: index 0 = 2021, index 1 = 2022, etc.
              availableYears.add((2021 + idx).toString());
            }
          });
        }
      });
    } else {
      // For specific school, only include years with data for that school
      const schoolData = cipData.enrollment.find(
        school => school.schoolName === schoolValue
      );

      if (schoolData && schoolData['All']) {
        schoolData['All'].forEach((count, idx) => {
          if (count && count > 0) {
            availableYears.add((2021 + idx).toString());
          }
        });
      }
    }

    setYearsWithData(availableYears);
  }, [cipEquityData, selectedSchool?.value, currentCip]);

  // Handle radio changes
  const handleYearChange = (event) => {
    const newValue = event.target.value;
    const newOption = YEAR_OPTIONS.find((o) => o.value === newValue);
    if (newOption) {
      setSelectedFilterIndicatorDate(newOption);
    }
  };

  // Validate the currently selected option or default to first with data
  const validSelectedOption = useMemo(() => {
    // First try to find the currently selected option
    const current = YEAR_OPTIONS.find(
      (opt) => opt.value === selectedFilterIndicatorDate?.value
    );
    if (current) return current;

    // Otherwise default to first option
    return YEAR_OPTIONS[0];
  }, [selectedFilterIndicatorDate?.value]);

  // Measure on layout changes
  useLayoutEffect(() => {
    const currentIndex = YEAR_OPTIONS.findIndex(
      (o) => o.value === validSelectedOption.value
    );
    if (currentIndex < 0) return;

    // Wait for refs to be populated
    if (!labelRefs.current[currentIndex]) {
      requestAnimationFrame(() => {
        if (labelRefs.current[currentIndex]) {
          setSliderStyles(prev => ({ ...prev }));
        }
      });
      return;
    }

    const labelEl = labelRefs.current[currentIndex];
    const containerEl = containerRef.current;
    if (!labelEl || !containerEl) return;

    const labelRect = labelEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    const left = labelRect.left - containerRect.left;
    const width = labelRect.width;

    setSliderStyles({ left, width });
  }, [validSelectedOption.value]);

  // Prepare refs array when options change
  useEffect(() => {
    labelRefs.current = labelRefs.current.slice(0, YEAR_OPTIONS.length);
    while (labelRefs.current.length < YEAR_OPTIONS.length) {
      labelRefs.current.push(null);
    }
  }, []);

  // Helper to check if a year has data
  const yearHasData = (yearValue) => {
    return yearsWithData.has(yearValue);
  };

  return (
    <div className="select-none">
      <div className="flex flex-col">
        <label className="text-sm font-medium mb-2">Select School Year:</label>

        {/* Selector */}
        <div className="flex items-center">
          <div
            ref={containerRef}
            className="relative flex items-center bg-zinc-100 rounded-full border-zinc-200 border p-1 shadow-inner"
          >
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 600, damping: 30 }}
              className="absolute top-1 bottom-1 bg-white rounded-full shadow-md z-0"
              style={{
                left: sliderStyles.left,
                width: sliderStyles.width,
              }}
            />

            <fieldset className="relative flex w-full rounded-full gap-x-1">
              <legend className="sr-only">Choose a Year</legend>
              {YEAR_OPTIONS.map((option, idx) => {
                const isSelected = validSelectedOption.value === option.value;
                const hasData = yearHasData(option.value);

                return (
                  <label
                    key={option.value}
                    ref={(el) => (labelRefs.current[idx] = el)}
                    className={`relative z-10 flex flex-1 items-center justify-center
                      rounded-full py-2 px-4 font-medium text-sm
                      transition-colors duration-200 ease-in-out cursor-pointer
                      ${
                        isSelected
                          ? "text-zinc-900"
                          : hasData
                            ? "text-zinc-600 hover:bg-zinc-200"
                            : "text-zinc-400 hover:bg-zinc-200"
                      }
                    `}
                    title={hasData ? undefined : "No data available for this year"}
                  >
                    <input
                      type="radio"
                      name="cipEquityYear"
                      value={option.value}
                      checked={isSelected}
                      onChange={handleYearChange}
                      className="sr-only"
                      aria-label={`Year ${option.label}`}
                    />
                    {option.label}
                  </label>
                );
              })}
            </fieldset>
          </div>
        </div>

        {/* Message when selected year has no data */}
        {!yearHasData(validSelectedOption.value) && yearsWithData.size > 0 && (
          <p className="text-amber-600 text-xs mt-2">
            No data available for {validSelectedOption.label}.
            Try selecting a different year.
          </p>
        )}
      </div>
    </div>
  );
};

export default CipEquityYearSelector;
